import type { components } from '../api/schema.gen'
import { apexAutomationGantry } from './apex-automation'

type Sourceable = components['schemas']['Sourceable']
type SourceableRef = components['schemas']['SourceableRef']

/**
 * Resolves a bare `SourceableRef` (id/kind/uom — what a line carries on the
 * wire) to the fuller fixture `Sourceable` (name, catalogSku) for display.
 * No real catalog/sourceable lookup endpoint exists yet (WS-2's `/catalog`
 * route is still a placeholder), so every vertical that needs to show a
 * human name next to a line (Cart, RFQ) resolves against this same UID-68
 * fixtures dataset instead of each re-deriving its own copy of this map.
 * Recommend a follow-up ticket: a real `GET /catalog/search`-style lookup
 * this module (and its two callers) can be replaced by in one place.
 */
const ALL_SOURCEABLES_BY_ID = new Map<string, Sourceable>(
  [
    ...apexAutomationGantry.catalogSourceables,
    ...apexAutomationGantry.extraCatalogSourceables,
    ...apexAutomationGantry.customSourceables,
  ].map((sourceable) => [sourceable.id, sourceable]),
)

/** Look up any known fixture Sourceable (catalog or custom) by id. */
export function findSourceableById(id: string): Sourceable | undefined {
  return ALL_SOURCEABLES_BY_ID.get(id)
}

/** Best-effort display label for a line — falls back to the literal
 * "Unnamed part" when the Sourceable isn't in the fixtures lookup (e.g. a
 * line added server-side from a real catalog once one exists) or is present
 * but carries no `name`.
 *
 * ADR-0095 D1: both branches used to emit `Item ${ref.id.slice(0, 8)}` — an
 * 8-hex UUID fragment leaked straight into customer-facing UI on every
 * caller (`/rfq`, `/cart`, `RfqListRow`). Fixed at the source rather than
 * with a second, narrower non-leaking helper: a parallel seam would leave
 * two divergent fallbacks for the same wire type and leave the leak live on
 * the other callers. The return type is unchanged (`string`), so this is a
 * rendered-text-only change — no call site needed updating, only the
 * existing tests' assertions of the old fallback string. */
export function displayNameFor(ref: SourceableRef): string {
  const sourceable = findSourceableById(ref.id)
  if (!sourceable) {
    return 'Unnamed part'
  }
  const name = sourceable.name ?? 'Unnamed part'
  return sourceable.catalogSku ? `${name} (${sourceable.catalogSku})` : name
}
